import { useEffect, useMemo, useState } from "react";

import { useBrowserNotificationPermission } from "./hooks/useBrowserNotificationPermission";
import { useAirshipChannel } from "./hooks/useAirshipChannel";
import { useAirshipPrivacy } from "./hooks/useAirshipPrivacy";
import type { AirshipLoaderProps, AirshipSDK } from "./types";
import { loadAirshipScript } from "./loadAirshipScript";

const sdkSrc = "https://aswpsdkeu.com/notify/v2/ua-sdk.min.js";
const workerUrl = "/airship-push-worker.js";

/**
 * React component that handles Airship SDK initialization and management.
 * Automatically manages push notification registration, privacy settings, and user consent.
 *
 * @property appKey - Airship application key for SDK initialization
 * @property token - Authentication token for Airship services
 * @property vapidPublicKey - VAPID public key for push notifications
 * @property hasPushConsent - Whether user has consented to push notifications
 * @property hasTrackingConsent - Whether user has consented to tracking features
 * @property userId - Optional user identifier for contact identification
 * @property brand - Brand identifier for user ID formatting
 * @property businessType - Business type ('B2B' or 'B2C') for user ID formatting
 */
export const AirshipLoader = (props: AirshipLoaderProps) => {
  const {
    appKey,
    token,
    vapidPublicKey,
    hasPushConsent,
    hasTrackingConsent,
    userId,
    brand,
    businessType,
  } = props;

  const [sdk, setSdk] = useState<AirshipSDK | undefined>();

  const notificationPermission =
    useBrowserNotificationPermission(hasPushConsent);

  useEffect(() => {
    if (!hasPushConsent) {
      return;
    }

    let isCancelled = false;

    const initialize = async () => {
      try {
        if (isCancelled) {
          return;
        }

        const sdkInstance = await loadAirshipScript({
          appKey,
          token,
          vapidPublicKey,
          sdkSrc,
          workerUrl,
        });

        if (isCancelled) {
          return;
        }

        if (sdkInstance) {
          window.airshipSDK = sdkInstance; // Store globally for debugging
          setSdk(sdkInstance);
        } else {
          console.warn("[Airship] SDK instance is null or undefined");
        }
      } catch (error) {
        console.error("[Airship] Initialization process failed:", error);
      }
    };

    initialize();

    return () => {
      isCancelled = true;
    };
  }, [hasPushConsent, appKey, token, vapidPublicKey]);

  const { isOptedIn } = useAirshipChannel(
    hasPushConsent,
    notificationPermission,
    sdk,
  );

  const userDetails = useMemo(
    () => ({
      brand,
      businessType,
      userId,
    }),
    [brand, businessType, userId],
  );

  useAirshipPrivacy(hasTrackingConsent, userDetails, sdk, isOptedIn);

  return null;
};
